import { WELL_COLS, WELL_ROWS, idx, isMatchable, type CellIndex, type GemKind, type Grid } from './types';
import { getMatchMin } from './rules';

/**
 * A single like-colored run that meets the match threshold. Runs are the unit
 * of match scoring — a 3-match and a 7-match are different events, not the
 * same event at different volumes, so the resolver has to know where each run
 * starts and stops.
 */
export interface MatchRun {
  readonly kind: GemKind;
  readonly cells: readonly CellIndex[];
}

/**
 * Every run at or above the match threshold, horizontal and vertical.
 *
 * An L or T shape yields two runs that share a corner cell. That is correct:
 * they are two events of different sizes, and the shared cell belongs to both.
 * Stone is skipped entirely — inert, so it neither matches nor interrupts a
 * run of a different color; it just breaks the run it sits inside.
 *
 * The threshold comes from core/rules rather than a literal, so the match-3
 * versus match-4 experiment is one switch rather than a refactor.
 */
export function findRuns(grid: Grid, matchMin = getMatchMin()): MatchRun[] {
  const runs: MatchRun[] = [];

  for (let r = 0; r < WELL_ROWS; r++) {
    let runStart = -1;
    let runKind: GemKind | null = null;
    for (let c = 0; c <= WELL_COLS; c++) {
      const cell = c < WELL_COLS ? grid[idx(r, c)] ?? null : null;
      const kind: GemKind | null = cell !== null && isMatchable(cell) ? cell : null;
      if (kind !== null && kind === runKind) continue;
      if (runKind !== null && c - runStart >= matchMin) {
        const cells: CellIndex[] = [];
        for (let k = runStart; k < c; k++) cells.push(idx(r, k));
        runs.push({ kind: runKind, cells });
      }
      runKind = kind;
      runStart = kind !== null ? c : -1;
    }
  }

  for (let c = 0; c < WELL_COLS; c++) {
    let runStart = -1;
    let runKind: GemKind | null = null;
    for (let r = 0; r <= WELL_ROWS; r++) {
      const cell = r < WELL_ROWS ? grid[idx(r, c)] ?? null : null;
      const kind: GemKind | null = cell !== null && isMatchable(cell) ? cell : null;
      if (kind !== null && kind === runKind) continue;
      if (runKind !== null && r - runStart >= matchMin) {
        const cells: CellIndex[] = [];
        for (let k = runStart; k < r; k++) cells.push(idx(k, c));
        runs.push({ kind: runKind, cells });
      }
      runKind = kind;
      runStart = kind !== null ? r : -1;
    }
  }

  return runs;
}

/** Union of every run's cells. Derived from findRuns, never computed twice. */
export function findMatches(grid: Grid): Set<CellIndex> {
  const out = new Set<CellIndex>();
  for (const run of findRuns(grid)) for (const c of run.cells) out.add(c);
  return out;
}

/** Row indices where all 7 columns are occupied. Stone counts as occupied. */
export function findFullRows(grid: Grid): number[] {
  const rows: number[] = [];
  for (let r = 0; r < WELL_ROWS; r++) {
    let full = true;
    for (let c = 0; c < WELL_COLS; c++) {
      if (grid[idx(r, c)] === null) {
        full = false;
        break;
      }
    }
    if (full) rows.push(r);
  }
  return rows;
}
